// quiron/frontend/src/components/QuironTipTapEditor.js

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Strikethrough from '@tiptap/extension-strike';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { marked } from 'marked';
import { useTranslation } from 'react-i18next';
import { useUser } from '../contexts/UserContext';
import { useNotification } from '../contexts/NotificationContext'; // Import useNotification

import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBold, faItalic, faStrikethrough as faStrike, faHeading,
  faQuoteRight, faListUl, faListOl, faTasks, faTableCellsLarge,
  faExpand, faCompress, faUpload, faCheck, faTimes, faChevronUp, faChevronDown,
  faTableColumns, faBarsStaggered, faTrash,
  faSave, faCheckCircle, faSpinner, faCopy, faPaste, faListCheck
} from '@fortawesome/free-solid-svg-icons';
import './QuironTipTapEditor.css';

const AUTOSAVE_DEBOUNCE_TIME = 2000;

const Toolbar = ({
  editor,
  toggleFullScreen,
  isFullScreen,
  saveStatus,
  t,
  globalAutosaveEnabled,
  onManualSave,
  canManuallySave
}) => {
  if (!editor) return null;

  const [showTableForm, setShowTableForm] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const fileInputRef = useRef(null);

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const src = e.target?.result;
      if (src) {
        editor.chain().focus().setImage({ src: String(src), alt: file.name }).run();
      }
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  const toggleTableForm = useCallback(() => {
    setShowTableForm(prev => !prev);
  }, []);

  const insertTable = useCallback(() => {
    editor.chain().focus().insertTable({ rows: Number(tableRows), cols: Number(tableCols), withHeaderRow: true }).run();
    setShowTableForm(false);
  }, [editor, tableRows, tableCols]);

  const toolbarCommands = [
    { action: () => editor.chain().focus().toggleBold().run(), icon: faBold, name: 'bold', title: t('boldTitle') },
    { action: () => editor.chain().focus().toggleItalic().run(), icon: faItalic, name: 'italic', title: t('italicTitle') },
    { action: () => editor.chain().focus().toggleStrike().run(), icon: faStrike, name: 'strike', title: t('strikeTitle') },
    { action: () => editor.chain().focus().toggleBlockquote().run(), icon: faQuoteRight, name: 'blockquote', title: t('blockquoteTitle') },
    { type: 'divider' },
    { action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(), icon: faHeading, name: 'heading', params: { level: 1 }, title: t('heading1Title'), text: 'H1' },
    { action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(), icon: faHeading, name: 'heading', params: { level: 2 }, title: t('heading2Title'), text: 'H2' },
    { action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(), icon: faHeading, name: 'heading', params: { level: 3 }, title: t('heading3Title'), text: 'H3' },
    { type: 'divider' },
    { action: triggerImageUpload, icon: faUpload, name: 'imageUpload', title: t('uploadImageTitle') },
    { type: 'divider' },
    { action: () => editor.chain().focus().toggleBulletList().run(), icon: faListUl, name: 'bulletList', title: t('bulletListTitle') },
    { action: () => editor.chain().focus().toggleOrderedList().run(), icon: faListOl, name: 'orderedList', title: t('orderedListTitle') },
    { action: () => editor.chain().focus().toggleTaskList().run(), icon: faListCheck, name: 'taskList', title: t('taskListTitle') },
    { action: toggleTableForm, icon: faTableCellsLarge, name: 'table', title: t('insertTableTitle') },
  ];

  return (
    <div className="quiron-editor-toolbar">
      <div className="toolbar-section toolbar-section-left">
        <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" style={{ display: 'none' }} />
        {toolbarCommands.map((cmd, index) => {
          if (cmd.type === 'divider') {
            return <span key={`divider-${index}`} className="toolbar-divider"></span>;
          }
          let isDisabled = false;
          if (cmd.name && !['imageUpload', 'table'].includes(cmd.name)) {
              const commandName = cmd.name === 'strike' ? 'toggleStrike' : `toggle${cmd.name.charAt(0).toUpperCase() + cmd.name.slice(1)}`;
              if (editor.can()?.[commandName]) {
                  isDisabled = !editor.can()[commandName](cmd.params);
              } else if (cmd.name === 'heading' && editor.can()?.toggleHeading) {
                  isDisabled = !editor.can().toggleHeading(cmd.params);
              }
          }
          let isActive = false;
          if (cmd.isActiveTest) {
              isActive = cmd.isActiveTest();
          } else if (cmd.name) {
              isActive = editor.isActive(cmd.name, cmd.params);
          }
          const buttonElement = (
              <button
                key={cmd.title || `cmd-${index}`}
                onClick={cmd.action}
                title={cmd.title}
                disabled={isDisabled}
                className={isActive ? 'is-active' : ''} // A classe base .quiron-editor-toolbar button já aplica estilos
              >
                <FontAwesomeIcon icon={cmd.icon} />
                {cmd.text && <span className="toolbar-button-text">{cmd.text}</span>}
              </button>
          );

          if (cmd.name === 'table') {
            return (
              <React.Fragment key={(cmd.title || 'table-group') + '-group'}>
                {buttonElement}
                {showTableForm && (
                  <div className="quiron-inline-form quiron-table-form">
                    <div className="table-dimension-control">
                      <span>L:</span>
                      <input type="number" value={tableRows} onChange={(e) => setTableRows(Math.max(1, parseInt(e.target.value,10) || 1))} min="1" aria-label={t('tableRowsLabel')}/>
                      <div className="dimension-buttons">
                        <button onClick={() => setTableRows(prev => prev + 1)} aria-label={t('increaseRowsLabel')}><FontAwesomeIcon icon={faChevronUp} /></button>
                        <button onClick={() => setTableRows(prev => Math.max(1, prev - 1))} aria-label={t('decreaseRowsLabel')}><FontAwesomeIcon icon={faChevronDown} /></button>
                      </div>
                    </div>
                    <span>×</span>
                    <div className="table-dimension-control">
                      <span>C:</span>
                      <input type="number" value={tableCols} onChange={(e) => setTableCols(Math.max(1, parseInt(e.target.value,10) || 1))} min="1" aria-label={t('tableColsLabel')}/>
                      <div className="dimension-buttons">
                        <button onClick={() => setTableCols(prev => prev + 1)} aria-label={t('increaseColsLabel')}><FontAwesomeIcon icon={faChevronUp} /></button>
                        <button onClick={() => setTableCols(prev => Math.max(1, prev - 1))} aria-label={t('decreaseColsLabel')}><FontAwesomeIcon icon={faChevronDown} /></button>
                      </div>
                    </div>
                    <button onClick={insertTable} title={t('createTableTitle')} className="table-action-button"><FontAwesomeIcon icon={faCheck} /></button>
                    <button onClick={() => setShowTableForm(false)} title={t('cancelButtonLabel')} className="table-action-button"><FontAwesomeIcon icon={faTimes} /></button>
                  </div>
                )}
              </React.Fragment>
            );
          }
          return buttonElement;
        })}
      </div>

      <div className="toolbar-section toolbar-section-right">
        {!globalAutosaveEnabled && canManuallySave && (
          <button
            onClick={onManualSave}
            title={t('recordButtonLabel')}
            className="quiron-manual-save-button" 
            disabled={saveStatus === 'saving'}
          >
            <FontAwesomeIcon icon={faSave} />
            <span style={{ marginLeft: '5px' }}>{t('recordButtonLabel')}</span>
          </button>
        )}

        <div className="save-status-indicator">
          {saveStatus === 'saving' && (
            <>
              <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '5px' }} />
              <span>{t('autosaveRecording')}</span>
            </>
          )}
          {saveStatus === 'saved' && (
            <>
              <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'var(--notification-success-bg)', marginRight: '5px' }} />
              <span>{t('autosaveRecorded')}</span>
            </>
          )}
          {saveStatus === 'error' && (
            <>
              <FontAwesomeIcon icon={faTimes} style={{ color: 'var(--notification-error-bg)', marginRight: '5px' }} />
              <span>{t('autosaveError')}</span>
            </>
          )}
        </div>

        <button
          onClick={toggleFullScreen}
          title={isFullScreen ? t('exitFullScreen') : t('enterFullScreen')}
          className="quiron-fullscreen-button" 
        >
          <FontAwesomeIcon icon={isFullScreen ? faCompress : faExpand} />
        </button>
      </div>
    </div>
  );
};

function ContextMenuComponent({ x, y, options, onClose }) { // Renamed from TableContextMenu for generic use
  if (!options || options.length === 0) return null;

  const handleAction = (action) => {
    if (typeof action === 'function') {
      action();
    }
    onClose();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
        // Updated class name to reflect generic context menu
        if (event.target.closest('.quiron-context-menu') === null) { 
            onClose();
        }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  return (
    <div
      className="quiron-context-menu" // Generic class name for styling
      style={{ top: y, left: x, position: 'fixed', zIndex: 1000 }}
      onClick={(e) => e.stopPropagation()}
    >
      {options.map((option, index) => {
        if (option.type === 'divider') {
          return <div key={`divider-${index}`} className="context-menu-divider"></div>;
        }
        return (
          <button
            key={option.label || `ctx-menu-item-${index}`}
            onClick={() => handleAction(option.action)}
            disabled={option.disabled}
            className="context-menu-item"
          >
            {option.label || "Action"}
          </button>
        );
      })}
    </div>
  );
};

const QuironTipTapEditor = ({
  value,
  onChange,
  placeholder,
  height = 200,
  specialty,
  consultationType,
  autosavePrefix = "content",
  enableAutosaveRestore = true,
  onRestore
}) => {
  const { t } = useTranslation();
  const { autosaveEnabled: globalAutosaveEnabled } = useUser();
  const { addNotification } = useNotification(); // For paste notifications
  const editorWrapperRef = useRef(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [currentHeight, setCurrentHeight] = useState(height);
  const [contextMenu, setContextMenu] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle');
  const autosaveTimeoutRef = useRef(null);
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);
  const [restorableContent, setRestorableContent] = useState(null);
  const restorationAttemptedRef = useRef(false);
  const isRestoringRef = useRef(false);

  const getAutosaveKey = useCallback(() => {
    if (specialty && consultationType && autosavePrefix) {
      return `quironAutosave_${autosavePrefix}_${specialty}_${consultationType}`;
    }
    return null;
  }, [specialty, consultationType, autosavePrefix]);

  const AUTOSAVE_KEY_DYNAMIC = getAutosaveKey();

  const performSaveRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] },
        code: false,
        codeBlock: false,
        strike: false,
      }),
      Markdown.configure({ html: true, linkify: true, breaks: true }),
      Link.configure({ openOnClick: true, autolink: true, linkOnPaste: true, validate: href => /^https?:\/\//.test(href) }),
      Image.configure({ inline: false, allowBase64: true }),
      Placeholder.configure({ placeholder: placeholder || t('startTypingPlaceholder') }),
      Table.configure({ resizable: true }), TableRow, TableHeader, TableCell, Strikethrough, TaskList,
      TaskItem.configure({ nested: true }),
    ],
    content: value ? marked(value) : '',
    onUpdate: ({ editor: currentEditor, transaction }) => {
      if (!transaction.docChanged || isRestoringRef.current) {
        return;
      }
      const markdown = currentEditor.storage.markdown.getMarkdown();
      if (markdown !== value) {
        onChange(markdown);
      }

      if (enableAutosaveRestore && globalAutosaveEnabled && AUTOSAVE_KEY_DYNAMIC && performSaveRef.current) {
        setSaveStatus('saving');
        if (autosaveTimeoutRef.current) {
          clearTimeout(autosaveTimeoutRef.current);
        }
        autosaveTimeoutRef.current = setTimeout(performSaveRef.current, AUTOSAVE_DEBOUNCE_TIME);
      } else if (!globalAutosaveEnabled) {
        if (saveStatus !== 'idle' && saveStatus !== 'saved') {
          setSaveStatus('idle');
        }
        if (autosaveTimeoutRef.current) {
            clearTimeout(autosaveTimeoutRef.current);
        }
      }
    },
    editorProps: {
      attributes: {
        class: 'ProseMirror quiron-tiptap-editable-area',
        spellCheck: "false",
      },
      handleDOMEvents: {
        contextmenu: (view, event) => {
            event.preventDefault();
            const { state } = view;
            const { selection } = state;
            const isTextSelected = !selection.empty;
            let menuOptions = [];

            if (isTextSelected) {
                menuOptions = [
                    { label: t('selectAll'), action: () => editor.chain().focus().selectAll().run() },
                    { label: t('copy'), action: () => {
                        const { from, to } = selection;
                        const selectedText = state.doc.textBetween(from, to, " ");
                        navigator.clipboard.writeText(selectedText)
                            .then(() => addNotification(t('copiedToClipboard'), 'success'))
                            .catch(err => {
                              console.error(t('errorCopying'), err);
                              addNotification(t('errorCopying'), 'error');
                            });
                      }},
                    { label: t('paste'), action: async () => {
                        try {
                            const textToPaste = await navigator.clipboard.readText();
                            if (textToPaste) {
                                editor.chain().focus().insertContent(textToPaste).run();
                                addNotification(t('pastedFromClipboard'), 'success');
                            }
                        } catch (err) {
                            console.error(t('errorPasting'), err);
                            addNotification(t('errorPastingEnablePermission'), 'error');
                        }
                    }},
                ];
            } else {
                 menuOptions = [
                    { label: t('selectAll'), action: () => editor.chain().focus().selectAll().run() },
                    { label: t('paste'), action: async () => {
                        try {
                            const textToPaste = await navigator.clipboard.readText();
                            if (textToPaste) {
                                editor.chain().focus().insertContent(textToPaste).run();
                                addNotification(t('pastedFromClipboard'), 'success');
                            }
                        } catch (err) {
                            console.error(t('errorPasting'), err);
                            addNotification(t('errorPastingEnablePermission'), 'error');
                        }
                    }},
                ];
            }
            
            // Context menu for table operations
            const $anchor = selection.$anchor;
            let inTable = false;
            for (let d = $anchor.depth; d > 0; d--) {
              if ($anchor.node(d).type.name === 'table') {
                inTable = true;
                break;
              }
            }

            if (inTable) {
              menuOptions.push({type: 'divider'});
              menuOptions.push({ label: t('addColumnBefore'), action: () => editor.chain().focus().addColumnBefore().run(), disabled: !editor.can().addColumnBefore() });
              menuOptions.push({ label: t('addColumnAfter'), action: () => editor.chain().focus().addColumnAfter().run(), disabled: !editor.can().addColumnAfter() });
              menuOptions.push({ label: t('deleteColumn'), action: () => editor.chain().focus().deleteColumn().run(), disabled: !editor.can().deleteColumn() });
              menuOptions.push({ type: 'divider' });
              menuOptions.push({ label: t('addRowBefore'), action: () => editor.chain().focus().addRowBefore().run(), disabled: !editor.can().addRowBefore() });
              menuOptions.push({ label: t('addRowAfter'), action: () => editor.chain().focus().addRowAfter().run(), disabled: !editor.can().addRowAfter() });
              menuOptions.push({ label: t('deleteRow'), action: () => editor.chain().focus().deleteRow().run(), disabled: !editor.can().deleteRow() });
              menuOptions.push({ type: 'divider' });
              menuOptions.push({ label: t('mergeOrSplit'), action: () => editor.chain().focus().mergeOrSplit().run(), disabled: !editor.can().mergeOrSplit() });
              menuOptions.push({ label: t('deleteTable'), action: () => editor.chain().focus().deleteTable().run(), disabled: !editor.can().deleteTable() });
            }

            setContextMenu({ x: event.clientX, y: event.clientY, options: menuOptions });
            return true;
          },
      },
    },
  });

  const performSave = useCallback(() => {
    if (!editor || !AUTOSAVE_KEY_DYNAMIC) {
      console.warn("Salvamento não pôde ser executado: editor ou chave de autosave indisponível.");
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
      return;
    }
    setSaveStatus('saving');
    try {
      const currentMarkdownContent = editor.storage.markdown.getMarkdown();
      localStorage.setItem(AUTOSAVE_KEY_DYNAMIC, currentMarkdownContent);
      setSaveStatus('saved');
      console.log(`Conteúdo salvo para ${AUTOSAVE_KEY_DYNAMIC}.`);
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error("Erro ao salvar conteúdo:", error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [editor, AUTOSAVE_KEY_DYNAMIC]);

  useEffect(() => {
    performSaveRef.current = performSave;
  }, [performSave]);

  useEffect(() => {
    if (enableAutosaveRestore && globalAutosaveEnabled && editor && AUTOSAVE_KEY_DYNAMIC && !restorationAttemptedRef.current && !isRestoringRef.current) {
      restorationAttemptedRef.current = true;
      const savedMarkdown = localStorage.getItem(AUTOSAVE_KEY_DYNAMIC);
      if (savedMarkdown) {
        const currentEditorMarkdown = editor.storage.markdown.getMarkdown();
        if (savedMarkdown.trim() !== (value || '').trim() && savedMarkdown.trim() !== currentEditorMarkdown.trim()) {
          setRestorableContent(savedMarkdown);
          setShowRestorePrompt(true);
        }
      }
    }
  }, [editor, AUTOSAVE_KEY_DYNAMIC, value, enableAutosaveRestore, globalAutosaveEnabled]);


  const handleRestore = () => {
    if (editor && restorableContent) {
      isRestoringRef.current = true;
      const htmlValue = marked(restorableContent);
      editor.commands.setContent(htmlValue, false);
      onChange(restorableContent);
      console.log(`Conteúdo restaurado de ${AUTOSAVE_KEY_DYNAMIC}`);
      isRestoringRef.current = false;
      if (onRestore) {
        onRestore();
      }
    }
    setShowRestorePrompt(false);
    setRestorableContent(null);
  };

  const handleDiscardRestore = () => {
    if (enableAutosaveRestore && globalAutosaveEnabled && AUTOSAVE_KEY_DYNAMIC) {
      localStorage.removeItem(AUTOSAVE_KEY_DYNAMIC);
      console.log(`Conteúdo de autosave para ${AUTOSAVE_KEY_DYNAMIC} descartado.`);
    }
    setShowRestorePrompt(false);
    setRestorableContent(null);
    if (editor) {
      isRestoringRef.current = true;
      const defaultHtmlValue = marked(value || '');
      editor.commands.setContent(defaultHtmlValue, false);
      onChange(value || '');
      isRestoringRef.current = false;
    }
  };

  useEffect(() => {
    if (editor && editor.isEditable && !showRestorePrompt && !isRestoringRef.current) {
      const currentMarkdown = editor.storage.markdown.getMarkdown();
      if (value !== currentMarkdown) {
        const newHtmlValue = marked(value || '');
        if (newHtmlValue !== editor.getHTML()) {
          editor.commands.setContent(newHtmlValue, true);
        }
      }
    }
  }, [value, editor, showRestorePrompt]);

  const toggleFullScreen = () => {
    if (!editorWrapperRef.current) return;
    if (!document.fullscreenElement) {
      editorWrapperRef.current.requestFullscreen().catch(err => {
        alert(`Error entering full screen: ${err.message} (${err.name})`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    setCurrentHeight(height);
  }, [height]);

  const editorWrapperClass = isFullScreen
    ? 'quiron-tiptap-editor-wrapper fullscreen'
    : 'quiron-tiptap-editor-wrapper';

  const editorStyle = !isFullScreen ? { minHeight: `${currentHeight}px`, resize: 'vertical', overflowY: 'auto' } : { height: '100%', display: 'flex', flexDirection: 'column' };

  const renderRestorePrompt = () => {
    if (!showRestorePrompt || !restorableContent) return null;
    return (
      <div className="quiron-restore-prompt">
        <div className="quiron-restore-prompt-content">
          <FontAwesomeIcon icon={faInfoCircle} className="quiron-restore-prompt-icon" />
          <p className="quiron-restore-prompt-text">
            {t('restorePromptMessage')}
          </p>
        </div>
        <div className="quiron-restore-prompt-actions">
          <button
            onClick={handleRestore}
            className="quiron-restore-prompt-button quiron-restore-button-confirm"
          >
            {t('restoreButtonYes')}
          </button>
          <button
            onClick={handleDiscardRestore}
            className="quiron-restore-prompt-button quiron-restore-button-discard"
          >
            {t('restoreButtonNo')}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div ref={editorWrapperRef} className={editorWrapperClass} style={editorStyle}>
      {editor && (
        <Toolbar
          editor={editor}
          toggleFullScreen={toggleFullScreen}
          isFullScreen={isFullScreen}
          saveStatus={saveStatus}
          t={t}
          globalAutosaveEnabled={globalAutosaveEnabled}
          onManualSave={performSave}
          canManuallySave={!!AUTOSAVE_KEY_DYNAMIC}
        />
      )}
      {renderRestorePrompt()}
      <EditorContent editor={editor} className={isFullScreen ? 'fullscreen-editor-content' : ''} />
      {contextMenu && (
        <ContextMenuComponent // Using the renamed generic component
          x={contextMenu.x}
          y={contextMenu.y}
          options={contextMenu.options}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
};

export default QuironTipTapEditor;